import { motion } from 'framer-motion';
import { Swords, Clock, Users, Trophy, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { GameIcon } from '@/components/ui/GameIcon';
import { formatCurrency, formatRelativeTime } from '@/lib/utils';
import { getGameName } from '@/lib/gameConfig';
import type { Wager } from '@/lib/api';

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'success' | 'destructive' | 'outline' }> = {
  open: { label: 'Open', variant: 'default' },
  agreed: { label: 'Agreed', variant: 'outline' },
  funding: { label: 'Funding', variant: 'outline' },
  locked: { label: 'Locked', variant: 'secondary' },
  in_progress: { label: 'In Progress', variant: 'default' },
  pending_verification: { label: 'Verifying', variant: 'secondary' },
  ready_to_collect: { label: 'Ready to Collect', variant: 'success' },
  settled: { label: 'Settled', variant: 'success' },
  disputed: { label: 'Disputed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
  voided: { label: 'Voided', variant: 'destructive' },
};

interface WagerCardProps {
  wager: Wager;
  variant?: 'default' | 'compact';
  showActions?: boolean;
  index?: number;
}

export function WagerCard({ wager, variant = 'default', showActions = true, index = 0 }: WagerCardProps) {
  const status = statusConfig[wager.status] || statusConfig.open;
  const isOpen = wager.status === 'open';
  const hasOpponent = !!wager.opponent;

  if (variant === 'compact') {
    return (
      <Link to={`/wagers/${wager.id}`}>
        <Card className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md">
          <CardContent className="flex items-center gap-4 p-4">
            <GameIcon gameCode={wager.gameCode} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium truncate">
                  {wager.creator.displayName}
                  {hasOpponent && ` vs ${wager.opponent?.displayName}`}
                </p>
              </div>
              <p className="text-sm text-muted-foreground truncate">{wager.wagerType}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-heading font-bold text-primary">
                {formatCurrency(wager.amount)}
              </span>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={`/wagers/${wager.id}`}>
        <Card className="group cursor-pointer overflow-hidden transition-all hover:border-primary/50 hover:shadow-lg">
          <CardContent className="p-0">
            {/* Header */}
            <div className="flex items-center justify-between border-b p-4">
              <div className="flex items-center gap-3">
                <GameIcon gameCode={wager.gameCode} size="lg" />
                <div>
                  <p className="font-medium capitalize">
                    {getGameName(wager.gameCode)}
                  </p>
                  <p className="text-sm text-muted-foreground">{wager.wagerType}</p>
                </div>
              </div>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>

            {/* Players */}
            <div className="flex items-center justify-between p-4">
              {/* Creator */}
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={wager.creator.avatarUrl} />
                  <AvatarFallback>{wager.creator.displayName[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{wager.creator.displayName}</p>
                  {(wager.creator as { rating?: number }).rating && (
                    <p className="text-sm text-muted-foreground">{(wager.creator as { rating?: number }).rating}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 text-muted-foreground">
                <Swords className="h-5 w-5" />
                <span className="text-sm">VS</span>
              </div>

              {/* Opponent */}
              {hasOpponent ? (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-medium">{wager.opponent?.displayName}</p>
                    {(wager.opponent as { rating?: number } | undefined)?.rating && (
                      <p className="text-sm text-muted-foreground">{(wager.opponent as { rating?: number } | undefined)?.rating}</p>
                    )}
                  </div>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={wager.opponent?.avatarUrl} />
                    <AvatarFallback>{wager.opponent?.displayName?.[0]}</AvatarFallback>
                  </Avatar>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-medium text-muted-foreground">Open Challenge</p>
                    <p className="text-sm text-muted-foreground">Waiting for opponent</p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/30">
                    <Users className="h-5 w-5 text-muted-foreground/50" />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t bg-muted/30 px-4 py-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Trophy className="h-4 w-4" />
                  <span className="font-heading font-bold text-foreground">
                    {formatCurrency(parseFloat(wager.amount) * 2)}
                  </span>
                  <span>pot</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{formatRelativeTime(wager.createdAt)}</span>
                </div>
              </div>

              {showActions && isOpen && (
                <Button
                  variant="gradient"
                  size="sm"
                  className="gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  Accept
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}

              {!isOpen && (
                <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}
